import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandCard from "@/components/brand/BrandCard";
import SearchBar from "@/components/SearchBar";
import PropertyCard from "@/components/PropertyCard";
import BrandInput, { BrandField } from "@/components/brand/BrandInput";
import ResultsViewToggle from "@/components/search/ResultsViewToggle";
import LazyMapView from "@/components/search/LazyMapView";
import { fetchProperties } from "@/lib/api";

// Same italic helper used on the homepage — keeps the editorial accent intact
// without re-introducing the lowercase treatment.
const Em = ({ children }) => (
  <span className="italic" style={{ fontFamily: "var(--font-accent)" }}>
    {children}
  </span>
);

const VelaSearch = () => {
  const [props, setProps] = useState([]);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";

  // View state — defaults to List; Map view is lazy-loaded on demand.
  const [view, setView] = useState("list");

  useEffect(() => { fetchProperties({ brand: "vela" }).then(setProps); }, []);
  const filtered = useMemo(
    () =>
      props.filter(
        (p) =>
          !q ||
          p.name.toLowerCase().includes(q.toLowerCase()) ||
          p.location.toLowerCase().includes(q.toLowerCase())
      ),
    [props, q]
  );

  return (
    <div className="container-rail py-10 md:py-14" data-testid="vela-search">
      <BrandLabel withRule>The Coast · Stays</BrandLabel>
      <BrandHeading level={1} className="mt-4">
        Five waterfront <Em>retreats</Em>.
      </BrandHeading>
      <p className="mt-3 max-w-[58ch] text-muted-foreground font-body">
        Choose by feel: an island house, a dune apartment, or a long-window suite above the bay.
      </p>
      <div className="mt-8"><SearchBar scope="vela" defaultBrand="vela" /></div>
      <div className="mt-10 grid lg:grid-cols-[280px_1fr] gap-10">
        <aside className="hidden lg:block">
          <BrandCard className="p-5 space-y-5">
            <BrandLabel>Filter</BrandLabel>
            <BrandField label="Destination">
              <BrandInput
                data-testid="vela-filter-destination"
                placeholder="Sylt, Rügen, Warnemünde…"
                defaultValue={q}
                onChange={(e) => {
                  const next = new URLSearchParams(params);
                  if (e.target.value) next.set("q", e.target.value);
                  else next.delete("q");
                  setParams(next, { replace: true });
                }}
              />
            </BrandField>
          </BrandCard>
        </aside>
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-[12px] text-muted-foreground">
              {filtered.length} of {props.length} stays
            </span>
            <ResultsViewToggle view={view} onChange={setView} />
          </div>
          {view === "map" ? (
            <div data-testid="vela-map-results">
              <LazyMapView items={filtered} initialBounds="baltic" height={620} />
              <div className="mt-3 text-[12px] text-muted-foreground">
                Showing {filtered.length} {filtered.length === 1 ? "stay" : "stays"} along the German Baltic coast.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vela-results">
              {filtered.map((p) => <PropertyCard key={p.id} item={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VelaSearch;
